import {
  Box,
  Button,
  FieldText,
  FlexBox,
  FormField,
  FormGrid,
  Page,
  Text,
} from '@dixa/lib-shared-habla';
import { config } from 'lib-shared-backend-config';
import { AsyncStatus } from 'lib-shared-legacy-types';
import { ampli } from 'lib-shared-tracking';
import { Organization as OrganizationT } from 'lib-shared-types-api';
import noop from 'lodash/noop';
import moment from 'moment-timezone';
import { Field, InjectedFormProps } from 'redux-form';
import { required } from 'redux-form-validators';

import { TimezoneSelector } from 'components/timezoneSelector';
import { dataUriToFile } from 'utils/file/dataUriToFile';

import AvatarControl from '../../../shared/avatarControl/components/AvatarControl';

import styles from './organization.module.scss';

export type OrganizationFormValuesT = {
  organization: {
    name: string;
    timezone?: string;
  };
};

export type OrganizationOwnPropsT = {};
export type OrganizationDispatchPropsT = {
  getOrganizationData: (organizationID: string) => void;
  updateAvatar: (file, organization) => void;
  editOrganization: (id: string, payload) => void;
};

export type OrganizationStatePropsT = {
  initialValues: OrganizationFormValuesT;
  meta: { asyncStatus: AsyncStatus };
  organization: OrganizationT;
  hasTimezoneSetting: boolean;
};

export type OrganizationPropsT = OrganizationOwnPropsT &
  OrganizationDispatchPropsT &
  OrganizationStatePropsT;

type InjectedProps = InjectedFormProps<
  OrganizationFormValuesT,
  OrganizationPropsT
>;

type PropsT = OrganizationPropsT & InjectedProps;

type OrganizationEditPayloadT = {
  updates: {
    name: string;
    timezone?: string;
  };
  deletes: string[];
};

export const Organization = (props: PropsT): JSX.Element => {
  const handleSaveClick = (values: OrganizationFormValuesT) => {
    ampli.settingsOrganizationFormSubmitted();

    if (values) {
      const { name, timezone } = values.organization;

      const payload: OrganizationEditPayloadT = {
        updates: {
          name,
        },
        deletes: [],
      };

      if (props.hasTimezoneSetting) {
        if (timezone) {
          payload.updates.timezone = timezone;
        } else {
          payload.deletes.push('timezone');
        }
      }

      props.editOrganization(props.organization.id, payload);
    }
  };

  const handleAvatarSelect = async (dataURI: string): Promise<void> => {
    ampli.settingsOrganizationLogoChanged();

    const file = await dataUriToFile(dataURI, 'avatar');
    props.updateAvatar(file, {
      id: props.organization.id,
    });
  };

  const {
    organization,
    pristine,
    submitting,
    invalid,
    handleSubmit,
    hasTimezoneSetting,
  } = props;

  return (
    <form onSubmit={handleSubmit(handleSaveClick)}>
      <Page.Section
        title="Info"
        controls={
          <Button disabled={pristine || submitting || invalid} type="submit">
            Save
          </Button>
        }
      >
        <Box margin="0 0 400 0">
          <AvatarControl
            type="org"
            user={organization}
            onSelectAvatar={handleAvatarSelect}
          />
        </Box>

        <FormGrid>
          <FormField span={3}>
            <Field
              label="Name"
              name="organization.name"
              component={FieldText}
              validate={[required()]}
              required
            />
          </FormField>

          <FormField span={3}>
            <FieldText
              label="Dixa URL"
              input={{
                name: 'url',
                value: `https://${organization.subdomain}.${config.domain.web}`,
                onChange: noop,
              }}
              readOnly
            />
          </FormField>
        </FormGrid>
      </Page.Section>

      {hasTimezoneSetting && (
        <Page.Section title="Preferences">
          <FormGrid>
            <FormField span={3}>
              <Field
                component={TimezoneSelector}
                name="organization.timezone"
                placeholder={`e.g. ${moment.tz.guess(true)}`}
                clearable
                helpIcon={{
                  title: 'What is this?',
                  text: (
                    <FlexBox flexDirection="column" gap="300">
                      <Text.Paragraph variant="2" color="white">
                        Your organization’s preferred timezone, which is used in
                        the following places:
                      </Text.Paragraph>

                      <FlexBox flexDirection="column" gap="100">
                        <Text.Heading variant="heading-3" color="white">
                          Dashboard
                        </Text.Heading>

                        <Text.Paragraph variant="2" color="white">
                          Your dashboard metrics will reset daily at midnight in
                          your chosen timezone, or at midnight UTC if no
                          timezone is set.
                        </Text.Paragraph>
                      </FlexBox>
                    </FlexBox>
                  ),
                  dark: true,
                  tooltipClassName: styles.timezoneTooltip,
                }}
              />
            </FormField>
          </FormGrid>
        </Page.Section>
      )}
    </form>
  );
};
